import React, { useEffect, useRef } from 'react';

interface CircleProgressProps {
    percentage: number;
    color?: string;
    size?: number;
    trailOpacity?: number;
    fontSize?: string;
    fillText?: boolean;
}

const CircleProgress: React.FC<CircleProgressProps> = ({
    percentage,
    color = '#0d6efd',
    size = 60,
    trailOpacity = 0.15,
    fontSize = '14px',
    fillText = false,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Convert hex color to rgba with opacity
    const getColorWithOpacity = (hexColor: string, opacity: number): string => {
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const centerX = size / 2;
        const centerY = size / 2;
        const radius = (size / 2) - 3;
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + (2 * Math.PI * percentage / 100);
        const trailColor = getColorWithOpacity(color, trailOpacity);

        // Clear canvas
        ctx.clearRect(0, 0, size, size);

        // Draw background circle (trail)
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.strokeStyle = trailColor;
        ctx.lineWidth = 6;
        ctx.stroke();

        // Draw progress arc
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.strokeStyle = color;
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.stroke();
        if (fillText) {
            ctx.font = `normal ${fontSize} Inter, sans-serif`;
            ctx.fillStyle = color;
            ctx.textAlign = 'center';
            ctx.fillText(`${Math.round(percentage)}%`, centerX, centerY + 4);
        }
    }, [percentage, color, size, trailOpacity]);

    return <canvas ref={canvasRef} width={size} height={size} className='align-middle' />;
};

export default CircleProgress;
